import React, { useState, useEffect, useCallback } from 'react';
import { Target, SlidersHorizontal, ShieldAlert } from 'lucide-react';
import { Card, Button, Pill, Input, Textarea, Select, Label, FieldGroup } from '../';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';

/**
 * NativeConformanceCard — #66 / conformance generalization.
 *
 * The conformance ("The Crossing" trust-baseline) surface for the Decide phase of
 * NATIVE agentlets (Let / AutoLet / SysLet), mirroring the ExteLet Decide
 * conformance block and reusing the SAME backend:
 *   getTrustScoringConfig · updateTrustScoringConfig (AGENT scope) ·
 *   listConformanceBaselines · registerConformanceBaseline · deleteConformanceBaseline.
 *
 * Oracle model per agentlet type (reference_extelet_conformance_oracle_gap):
 *   • AutoLet — the SOURCE WORKFLOW is the deterministic oracle; the shadow loop
 *     scores live behavior against a replay. A golden baseline is OPTIONAL.
 *   • Let / SysLet — cognitive, no deterministic source, so (like an ExteLet) the
 *     oracle is a GOLDEN baseline: representative input → expected output.
 *
 * Self-contained: loads + saves its own config; advisory (never blocks the gate).
 *
 * @param {Object}   props.agent     needs id, tenantId, orgId; sourceWorkflowId / isRunbookManaged for AutoLet detection
 * @param {Function} [props.onChanged] called after a successful save so the parent can refresh the gate
 */
export default function NativeConformanceCard({ agent, onChanged }) {
  const agentId = agent?.id;
  const tenantId = agent?.tenantId;
  const orgId = agent?.orgId;
  const isAutoLet = !!agent?.sourceWorkflowId || agent?.isRunbookManaged === true
    || (agent?.archetype || '').toLowerCase() === 'autolet';

  const toastCtx = useToast();
  const addToast = useCallback((arg) => toastCtx?.addToast?.(arg), [toastCtx]);

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [baselineForm, setBaselineForm] = useState({
    conformanceComparatorMode: 'exact_match',
    requiredConformance: '90',
    conformanceSampleSize: '50',
    conformanceWeight: '0.25',
    shadowConformanceEnabled: 'true',
  });
  const [savingBaseline, setSavingBaseline] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!tenantId || !agentId) { setConfigLoading(false); return; }
    setConfigLoading(true);
    try {
      const res = await api.getTrustScoringConfig(tenantId, 'AGENT', agentId);
      const cfg = res?.data ?? null;
      setConfig(cfg);
      if (cfg) {
        setBaselineForm((prev) => ({
          conformanceComparatorMode: cfg.conformanceComparatorMode ?? prev.conformanceComparatorMode,
          requiredConformance: String(cfg.requiredConformance ?? prev.requiredConformance),
          conformanceSampleSize: String(cfg.conformanceSampleSize ?? prev.conformanceSampleSize),
          conformanceWeight: String(cfg.conformanceWeight ?? prev.conformanceWeight),
          shadowConformanceEnabled: String(cfg.shadowConformanceEnabled ?? prev.shadowConformanceEnabled),
        }));
      }
    } catch (e) {
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  }, [tenantId, agentId]);

  // Golden baseline (reference oracle)
  const [goldenForm, setGoldenForm] = useState({ input: '', goldenOutput: '', comparatorMode: '', description: '' });
  const [goldenList, setGoldenList] = useState([]);
  const [goldenLoading, setGoldenLoading] = useState(true);
  const [savingGolden, setSavingGolden] = useState(false);

  const loadGolden = useCallback(async () => {
    if (!agentId) { setGoldenLoading(false); return; }
    setGoldenLoading(true);
    try {
      const res = await api.listConformanceBaselines(agentId);
      setGoldenList(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
    } catch (e) {
      setGoldenList([]);
    } finally {
      setGoldenLoading(false);
    }
  }, [agentId]);

  useEffect(() => { loadConfig(); loadGolden(); }, [loadConfig, loadGolden]);

  const num = (v, d) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? d : Number(v));

  const handleRegisterBaseline = useCallback(async () => {
    if (!tenantId || !orgId || !agentId) {
      addToast({ tone: 'danger', title: 'Missing scope', description: 'Agent tenant/org not resolved yet.' });
      return;
    }
    const base = config || {};
    // AGENT-scope override is a COMPLETE row (inheritance happens at resolution time);
    // preserve the inherited scoring knobs and set only the conformance fields.
    const payload = {
      configScope: 'AGENT', tenantId, orgId, agentId,
      evaluationMultiplier: num(base.evaluationMultiplier, 1),
      blockWeight: num(base.blockWeight, 3),
      warnWeight: num(base.warnWeight, 1),
      auditWeight: num(base.auditWeight, 0.25),
      selfAutonomousThreshold: num(base.selfAutonomousThreshold, 98),
      selfAutonomousMinEvals: num(base.selfAutonomousMinEvals, 500),
      supervisedThreshold: num(base.supervisedThreshold, 90),
      restrictedThreshold: num(base.restrictedThreshold, 75),
      windowDays: num(base.windowDays, 90),
      probationDays: num(base.probationDays, 7),
      trustHistoryRetentionDays: num(base.trustHistoryRetentionDays, 1095),
      minEvalsForScoring: num(base.minEvalsForScoring, 50),
      scoreFloor: num(base.scoreFloor, 5),
      anomalyCooldownHours: num(base.anomalyCooldownHours, 6),
      autoRegressionOnBlock: base.autoRegressionOnBlock ?? true,
      targetTrustScore: num(base.targetTrustScore, 95),
      targetAutonomyLevel: base.targetAutonomyLevel || 'SUPERVISED',
      orgRiskTolerance: base.orgRiskTolerance || 'MODERATE',
      shadowSamplingRate: num(base.shadowSamplingRate, 1),
      conformanceComparatorMode: baselineForm.conformanceComparatorMode,
      requiredConformance: num(baselineForm.requiredConformance, 90),
      conformanceSampleSize: parseInt(baselineForm.conformanceSampleSize, 10) || 50,
      conformanceWeight: num(baselineForm.conformanceWeight, 0.25),
      shadowConformanceEnabled: baselineForm.shadowConformanceEnabled === 'true'
        || baselineForm.shadowConformanceEnabled === true,
    };
    setSavingBaseline(true);
    try {
      const res = await api.updateTrustScoringConfig(payload);
      if (res && res.ok === false) throw new Error(res.error || 'Failed to save conformance settings');
      addToast({ tone: 'success', title: 'Conformance settings saved',
        description: 'The agent will be scored against this baseline once live.' });
      await loadConfig();
      onChanged?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not save conformance settings', description: e?.message || String(e) });
    } finally {
      setSavingBaseline(false);
    }
  }, [tenantId, orgId, agentId, config, baselineForm, addToast, loadConfig, onChanged]);

  const handleRegisterGolden = useCallback(async () => {
    let input; let goldenOutput;
    try { input = JSON.parse(goldenForm.input); }
    catch (e) { addToast({ tone: 'danger', title: 'Invalid input JSON', description: 'The representative input must be valid JSON.' }); return; }
    try { goldenOutput = JSON.parse(goldenForm.goldenOutput); }
    catch (e) { addToast({ tone: 'danger', title: 'Invalid expected-output JSON', description: 'The expected output must be valid JSON.' }); return; }
    setSavingGolden(true);
    try {
      const body = { input, goldenOutput, comparatorMode: goldenForm.comparatorMode || null, description: goldenForm.description || null };
      const res = await api.registerConformanceBaseline(agentId, body);
      if (res && res.ok === false) throw new Error(res.error || 'Failed to register golden baseline');
      addToast({ tone: 'success', title: 'Golden baseline registered',
        description: 'Matching protected calls will now be conformance-scored against this output.' });
      setGoldenForm((p) => ({ ...p, input: '', goldenOutput: '', description: '' }));
      await loadGolden();
      onChanged?.();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not register golden baseline', description: e?.message || String(e) });
    } finally {
      setSavingGolden(false);
    }
  }, [agentId, goldenForm, addToast, loadGolden, onChanged]);

  const handleDeleteGolden = useCallback(async (baselineId) => {
    try {
      const res = await api.deleteConformanceBaseline(agentId, baselineId);
      if (res && res.ok === false) throw new Error(res.error || 'Delete failed');
      await loadGolden();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not delete baseline', description: e?.message || String(e) });
    }
  }, [agentId, addToast, loadGolden]);

  const oracleReady = isAutoLet || goldenList.length > 0;

  return (
    <Card padding="none">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
        <Target size={16} className="text-v2-cyan" />
        <h3 className="text-[14px] font-bold tracking-tight m-0">Trust baseline (conformance)</h3>
        <Pill tone={oracleReady ? 'success' : 'muted'} size="sm">
          {isAutoLet ? 'Source-workflow oracle' : goldenList.length > 0 ? `${goldenList.length} golden registered` : 'No oracle yet'}
        </Pill>
        <a href="/native-trust?tab=scoring"
          className="ml-auto v2-focus-ring text-[11.5px] font-semibold text-v2-action hover:text-v2-action-strong">
          Edit full scoring config &rarr;
        </a>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-[12.5px] text-v2-ink-muted m-0">
          The conformance baseline (The Crossing) is the reference this agent's live behavior is scored
          against; conformance is blended into the unified Trust Score during Operate. Advisory — it never
          blocks promotion.
        </p>

        {/* Oracle status — differs by agentlet type */}
        <div className="rounded-v2-md px-3 py-2 text-[12px] flex items-start gap-2"
          style={{
            background: oracleReady ? 'var(--v2-success-soft)' : 'var(--v2-warning-soft)',
            color: oracleReady ? 'var(--v2-success-strong)' : 'var(--v2-warning)',
          }}>
          <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {isAutoLet ? (
              <>
                <strong>This AutoLet's source workflow is its conformance oracle.</strong> The shadow loop
                replays the source workflow and scores live behavior against it — no golden baseline required.
                You may still register golden baselines below for specific input → output checks.
              </>
            ) : goldenList.length > 0 ? (
              <>
                <strong>{goldenList.length} golden baseline{goldenList.length === 1 ? '' : 's'} registered.</strong>{' '}
                Governed calls whose input matches are scored against the expected output. Conformance affects
                the Trust Score only once <strong>Conformance weight &gt; 0</strong>.
              </>
            ) : (
              <>
                <strong>No oracle yet — conformance is inert.</strong> This agent is cognitive (no deterministic
                source workflow), so register a golden baseline below (representative input → expected output).
                Until then the settings save but there is nothing to score against.
              </>
            )}
          </span>
        </div>

        {/* Conformance settings */}
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <FieldGroup>
            <Label htmlFor="ncf-mode">Comparator</Label>
            <Select id="ncf-mode" value={baselineForm.conformanceComparatorMode}
              onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceComparatorMode: e.target.value }))}>
              <option value="exact_match">Exact match</option>
              <option value="equivalence">Equivalence</option>
              <option value="tolerance_banded">Tolerance banded</option>
              <option value="custom">Custom</option>
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="ncf-required">Required conformance (%)</Label>
            <Input id="ncf-required" type="number" min="0" max="100" value={baselineForm.requiredConformance}
              onChange={(e) => setBaselineForm((p) => ({ ...p, requiredConformance: e.target.value }))} />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="ncf-weight">Conformance weight (0–1)</Label>
            <Input id="ncf-weight" type="number" min="0" max="1" step="0.05" value={baselineForm.conformanceWeight}
              onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceWeight: e.target.value }))} />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="ncf-sample">Sample size</Label>
            <Input id="ncf-sample" type="number" min="1" value={baselineForm.conformanceSampleSize}
              onChange={(e) => setBaselineForm((p) => ({ ...p, conformanceSampleSize: e.target.value }))} />
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="ncf-shadow">Shadow conformance</Label>
            <Select id="ncf-shadow" value={baselineForm.shadowConformanceEnabled}
              onChange={(e) => setBaselineForm((p) => ({ ...p, shadowConformanceEnabled: e.target.value }))}>
              <option value="true">On</option>
              <option value="false">Off</option>
            </Select>
          </FieldGroup>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" loading={savingBaseline} onClick={handleRegisterBaseline}>
            {config ? 'Update conformance settings' : 'Save conformance settings'}
          </Button>
          {configLoading && <span className="text-[11.5px] text-v2-ink-muted">Loading…</span>}
        </div>

        {/* Golden baseline (reference oracle) */}
        <div className="border-t border-v2-border-subtle pt-3 mt-1 space-y-3">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={14} className="text-v2-cyan" />
            <h4 className="text-[12.5px] font-bold m-0">
              Golden baseline {isAutoLet ? '(optional — source workflow is the primary oracle)' : '(reference oracle)'}
            </h4>
            <Pill tone={goldenList.length > 0 ? 'success' : 'muted'} size="sm">
              {goldenLoading ? '…' : `${goldenList.length} registered`}
            </Pill>
          </div>
          <p className="text-[11.5px] text-v2-ink-muted m-0">
            Register a representative input and the output it should produce. On a protected call whose input
            matches, the agent's output is scored against this expected output — no re-execution.
          </p>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <FieldGroup>
              <Label htmlFor="ngb-input">Representative input (JSON)</Label>
              <Textarea id="ngb-input" rows={5} placeholder={'{"recordId": 42, "action": "submit"}'}
                value={goldenForm.input}
                onChange={(e) => setGoldenForm((p) => ({ ...p, input: e.target.value }))} />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="ngb-output">Expected output (JSON)</Label>
              <Textarea id="ngb-output" rows={5} placeholder={'{"status": "accepted"}'}
                value={goldenForm.goldenOutput}
                onChange={(e) => setGoldenForm((p) => ({ ...p, goldenOutput: e.target.value }))} />
            </FieldGroup>
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <FieldGroup>
              <Label htmlFor="ngb-comparator">Comparator override (optional)</Label>
              <Select id="ngb-comparator" value={goldenForm.comparatorMode}
                onChange={(e) => setGoldenForm((p) => ({ ...p, comparatorMode: e.target.value }))}>
                <option value="">Use agent default</option>
                <option value="exact_match">Exact match</option>
                <option value="equivalence">Equivalence</option>
                <option value="tolerance_banded">Tolerance banded</option>
              </Select>
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="ngb-desc">Description (optional)</Label>
              <Input id="ngb-desc" value={goldenForm.description}
                onChange={(e) => setGoldenForm((p) => ({ ...p, description: e.target.value }))} />
            </FieldGroup>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" loading={savingGolden}
              disabled={!goldenForm.input.trim() || !goldenForm.goldenOutput.trim()}
              onClick={handleRegisterGolden}>
              Register golden baseline
            </Button>
          </div>

          {goldenList.length > 0 && (
            <div className="space-y-1">
              {goldenList.map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-[11.5px] py-1 border-b border-v2-border-subtle/50">
                  <span className="font-mono-v2 text-v2-ink-muted">{String(b.inputHash || '').slice(0, 10)}…</span>
                  <span className="text-v2-ink-secondary truncate">{b.description || '(no description)'}</span>
                  <span className="ml-auto text-v2-ink-muted">{b.comparatorMode || 'default'}</span>
                  <button className="v2-focus-ring text-[11.5px] font-semibold text-v2-danger hover:text-v2-danger-strong"
                    onClick={() => handleDeleteGolden(b.id)} title="Delete this golden baseline">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
